import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, CheckCircle2, Eye, EyeOff, Lock, Mail, LogIn, ShieldCheck, UserPlus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isRegistering && password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    if (isForgotPassword) {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Check your email for a password reset link.');
        setIsForgotPassword(false);
      }
      setLoading(false);
      return;
    }

    if (isRegistering) {
      const signUpData: any = {
        full_name: fullName,
        role: 'staff',
      };

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: signUpData,
        },
      });

      if (error) {
        toast.error(error.message);
      } else if (data.session) {
        toast.success('Account created successfully');
        navigate('/');
      } else {
        toast.success('Account created. Check your email to confirm your account.');
        setIsRegistering(false);
      }
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Logged in successfully');
        navigate('/');
      }
    }
    setLoading(false);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 px-4 py-6 text-slate-900 sm:p-8">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_5%_15%,rgba(37,99,235,0.38),transparent_28%),radial-gradient(circle_at_95%_80%,rgba(20,184,166,0.22),transparent_25%)]" />
      <div className="relative mx-auto grid min-h-[calc(100vh-3rem)] max-w-6xl overflow-hidden rounded-3xl border border-white/15 bg-white/10 shadow-2xl shadow-black/40 backdrop-blur-md lg:grid-cols-[0.95fr_1.05fr]">
        <aside className="hidden flex-col justify-between bg-slate-900/45 p-10 text-white lg:flex">
          <div>
            <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-sm font-medium text-slate-300 transition hover:text-white">
              <ArrowLeft size={16} /> Back to welcome
            </button>
            <div className="mt-14 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-primary-700 shadow-lg">
                <Building2 size={25} />
              </div>
              <div>
                <p className="font-bold tracking-wide">LevyLedger</p>
                <p className="text-sm text-slate-400">Body Corporate Management</p>
              </div>
            </div>
            <h1 className="mt-12 text-4xl font-bold leading-tight">A clearer way to manage every levy account.</h1>
            <p className="mt-5 max-w-sm text-lg leading-8 text-slate-300">Secure records, professional statements and a reliable ledger for your scheme.</p>
          </div>
          <div className="space-y-4 text-sm text-slate-300">
            <p className="flex items-center gap-3"><CheckCircle2 size={18} className="text-teal-300" /> Accurate account balances</p>
            <p className="flex items-center gap-3"><CheckCircle2 size={18} className="text-teal-300" /> Professional PDF statements</p>
            <p className="flex items-center gap-3"><ShieldCheck size={18} className="text-teal-300" /> Role-based secure access</p>
          </div>
        </aside>

        <section className="flex items-center justify-center bg-slate-50 p-6 sm:p-10">
          <div className="w-full max-w-md">
            <button onClick={() => navigate('/')} className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-primary-700 lg:hidden">
              <ArrowLeft size={16} /> Back to welcome
            </button>
            <div className="mb-8">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-100 text-primary-700 lg:hidden">
                <Building2 size={23} />
              </div>
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary-700">Body Corporate Levy</p>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
                {isForgotPassword ? 'Reset your password' : isRegistering ? 'Request your account' : 'Welcome back'}
              </h2>
              <p className="mt-2 leading-6 text-slate-500">
            {isForgotPassword
              ? 'Enter your email to receive a password reset link'
              : isRegistering
                ? 'Create your access request. An administrator will assign your scheme access.'
                : 'Sign in to your secure levy management workspace.'}
              </p>
            </div>

        <form onSubmit={handleSubmit} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="space-y-4">
            {isRegistering && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  className="w-full rounded-xl border border-slate-300 py-2.5 pl-10 pr-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {!isForgotPassword && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    className="w-full rounded-xl border border-slate-300 py-2.5 pl-10 pr-10 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}

            {!isForgotPassword && !isRegistering && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {!isForgotPassword && isRegistering && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat your password"
                    className="w-full rounded-xl border border-slate-300 py-2.5 pl-10 pr-10 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide confirmed password' : 'Show confirmed password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 py-3 font-semibold text-white shadow-lg shadow-primary-200 transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? (
              isForgotPassword ? 'Sending reset link...' : isRegistering ? 'Creating account...' : 'Signing in...'
            ) : (
              <>
                {isForgotPassword ? <Mail size={20} /> : isRegistering ? <UserPlus size={20} /> : <LogIn size={20} />}
                {isForgotPassword ? 'Send Reset Link' : isRegistering ? 'Create Account' : 'Sign In'}
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center text-sm">
          {isForgotPassword ? (
            <button
              type="button"
              onClick={() => setIsForgotPassword(false)}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Back to sign in
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setIsRegistering(!isRegistering)}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              {isRegistering ? 'Already have an account? Sign in' : 'Need an account? Register'}
            </button>
          )}
        </div>
            <p className="mt-8 flex items-center justify-center gap-2 text-xs text-slate-400"><Lock size={13} /> Your information is protected by secure sign-in.</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Login;
